import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  RequestQuoteOutlined,
  Search as SearchIcon,
} from '@mui/icons-material';
import { quoteRequestService } from '../../services/quoteRequestService';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  QUOTE_REQUEST_STATUS_META,
  SalesCard,
  SalesEmptyState,
  SalesPageHeader,
  SalesStatCard,
  SalesStatusChip,
  SALES,
  salesPrimaryButtonSx,
  salesRowHoverSx,
  salesTableHeadSx,
} from '../../components/sales/salesUi';

const SalesQuoteRequests = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await quoteRequestService.getAll({
        search: search || undefined,
        status: status || undefined,
        limit: 100,
      });
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => ({
    draft: rows.filter((r) => r.status === 'draft').length,
    pending: rows.filter((r) => ['submitted', 'in_review'].includes(r.status)).length,
    approved: rows.filter((r) => r.status === 'approved').length,
    rejected: rows.filter((r) => r.status === 'rejected').length,
  }), [rows]);

  const targetLabel = (row) => {
    if (row.lead_id) return `Lead #${row.lead_number} — ${row.lead_company_name || ''}`;
    if (row.customer_id) return `Customer #${row.customer_number} — ${row.customer_company_name || ''}`;
    return '—';
  };

  return (
    <Box>
      <SalesPageHeader
        title="Quote Requests"
        subtitle="Submit potential quotes for Admin review. Approved requests become real Quotes in the main system."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              sx={{ textTransform: 'none', borderColor: SALES.line, color: SALES.ink }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/sales/quote-requests/new')}
              sx={salesPrimaryButtonSx}
            >
              New Request
            </Button>
          </>
        }
      />

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={3}>
          <SalesStatCard label="Drafts" value={stats.draft} hint="Still editable" />
        </Grid>
        <Grid item xs={6} md={3}>
          <SalesStatCard label="Awaiting Review" value={stats.pending} hint="Submitted to Admin" accent={SALES.info} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SalesStatCard label="Approved" value={stats.approved} hint="Became Quotes" accent={SALES.success} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SalesStatCard label="Rejected" value={stats.rejected} hint="Needs revision" accent={SALES.danger} />
        </Grid>
      </Grid>

      <SalesCard sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search request #, company, description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: SALES.muted, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All statuses</MenuItem>
              {Object.entries(QUOTE_REQUEST_STATUS_META).map(([key, meta]) => (
                <MenuItem key={key} value={key}>{meta.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </SalesCard>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <SalesEmptyState
          icon={<RequestQuoteOutlined sx={{ fontSize: 42 }} />}
          title="No quote requests yet"
          description="Build a request against one of your leads or an existing customer, then submit it for Admin review."
          actionLabel="New Quote Request"
          onAction={() => navigate('/sales/quote-requests/new')}
        />
      ) : (
        <TableContainer component={SalesCard}>
          <Table size="small">
            <TableHead sx={salesTableHeadSx}>
              <TableRow>
                <TableCell>Request #</TableCell>
                <TableCell>For</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={salesRowHoverSx}
                  onClick={() => navigate(`/sales/quote-requests/${row.id}`)}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, color: SALES.steel }}>
                      #{row.request_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>{targetLabel(row)}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={row.description || ''}>
                      {row.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <SalesStatusChip status={row.status} metaMap={QUOTE_REQUEST_STATUS_META} />
                  </TableCell>
                  <TableCell>
                    {row.submitted_at ? formatDateDisplay(row.submitted_at) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SalesQuoteRequests;
